import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

// --- Interfaces ---

export interface EventItem {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  venue?: { id: string; name: string; parkingInfo?: string | null };
  venueId: string;
  _count?: { registrations: number };
}

export interface AggregateStats {
  totalEvents: number;
  uniqueDates: number;
  uniqueVenues: number;
}

// --- Helper Functions ---

export function groupEventsByDate(events: EventItem[]): Map<string, EventItem[]> {
  const sorted = [...events].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });

  const groups = new Map<string, EventItem[]>();
  for (const evt of sorted) {
    const key = evt.date.split('T')[0];
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(evt);
  }
  return groups;
}

export function computeAggregates(events: EventItem[]): AggregateStats {
  const totalEvents = events.length;
  const uniqueDates = new Set(events.map(e => e.date.split('T')[0])).size;
  const uniqueVenues = new Set(
    events.filter(e => e.venue).map(e => e.venue!.id)
  ).size;
  return { totalEvents, uniqueDates, uniqueVenues };
}

export function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function getCapacityPercentage(registrations: number, maxCapacity: number): number {
  if (maxCapacity <= 0) return 0;
  return Math.min((registrations / maxCapacity) * 100, 100);
}

// --- Accent Colors ---

const ACCENT_COLORS = [
  '#c9a84c', '#1a6b3c', '#2563eb', '#7c3aed',
  '#be185d', '#0891b2', '#dc2626', '#059669',
];

// --- Component ---

export function EventSnapshotsPage() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [infoPopup, setInfoPopup] = useState<{ eventId: string; agenda: { title: string; startTime: string; endTime: string; speaker?: string }[]; parkingInfo: string | null; loading: boolean } | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const data = await apiClient<EventItem[]>('/events');
        setEvents(data);
      } catch (err: unknown) {
        const apiError = err as { message?: string };
        setError(apiError.message || 'Failed to load events.');
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
    // Org name for the header (tenant-specific, not hardcoded).
    apiClient<{ tenantName?: string | null }>('/auth/me')
      .then((r) => setOrgName(r?.tenantName ?? null))
      .catch(() => setOrgName(null));
  }, []);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function handleInfoClick(evt: EventItem, e: React.MouseEvent) {
    e.stopPropagation();
    // If already showing this event's popup, close it
    if (infoPopup?.eventId === evt.id && !infoPopup.loading) {
      setInfoPopup(null);
      return;
    }
    // Show loading state
    setInfoPopup({ eventId: evt.id, agenda: [], parkingInfo: evt.venue?.parkingInfo || null, loading: true });
    try {
      const agenda = await apiClient<{ title: string; startTime: string; endTime: string; speaker?: string }[]>(`/events/${evt.id}/agenda`);
      setInfoPopup({ eventId: evt.id, agenda, parkingInfo: evt.venue?.parkingInfo || null, loading: false });
    } catch {
      setInfoPopup({ eventId: evt.id, agenda: [], parkingInfo: evt.venue?.parkingInfo || null, loading: false });
    }
  }

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a1f14',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(201,168,76,0.3)',
            borderTopColor: '#c9a84c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#a3b8a9', fontSize: '15px' }}>Loading event snapshots...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a1f14',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '32px',
          backgroundColor: '#132b1e',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.1)',
          maxWidth: '400px',
        }}>
          <p style={{ color: '#dc2626', fontSize: '16px', marginBottom: '8px' }}>⚠️ Error</p>
          <p style={{ color: '#a3b8a9', fontSize: '14px', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const grouped = groupEventsByDate(events);
  const aggregates = computeAggregates(events);

  // Build flattened list for index-based alternation
  let flatIndex = 0;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a1f14',
      padding: '40px 24px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: '#f0f4f1',
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 800, marginBottom: '8px', margin: 0 }}>
            {orgName ? `${orgName} ` : ''}<span style={{ color: '#c9a84c' }}>Event Snapshots</span>
          </h1>
          <p style={{ color: '#a3b8a9', fontSize: '15px', marginTop: '8px' }}>
            Live event data · {aggregates.totalEvents} Events · {aggregates.uniqueDates} Days · {aggregates.uniqueVenues} Venues
          </p>
        </div>

        {/* Timeline */}
        <div style={{ position: 'relative' }}>
          {/* Vertical center line */}
          <div style={{
            position: 'absolute',
            left: isMobile ? '20px' : '50%',
            top: 0,
            bottom: 0,
            width: '2px',
            background: 'linear-gradient(to bottom, #1a6b3c, #c9a84c, #1a6b3c)',
            transform: isMobile ? 'none' : 'translateX(-50%)',
          }} />

          {Array.from(grouped.entries()).map(([dateKey, dayEvents]) => (
            <div key={dateKey}>
              {/* Date label pill */}
              <div style={{
                textAlign: isMobile ? 'left' : 'center',
                position: 'relative',
                zIndex: 2,
                margin: '40px 0 24px',
                paddingLeft: isMobile ? '48px' : '0',
              }}>
                <span style={{
                  backgroundColor: '#c9a84c',
                  color: '#0f2d1f',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '1.5px',
                  padding: '6px 20px',
                  borderRadius: '20px',
                  display: 'inline-block',
                }}>
                  {formatDateLabel(dateKey)}
                </span>
              </div>

              {/* Event cards for this day */}
              {dayEvents.map((evt) => {
                const currentIndex = flatIndex++;
                const isLeft = currentIndex % 2 === 0;
                const accentColor = ACCENT_COLORS[currentIndex % ACCENT_COLORS.length];
                const registrations = evt._count?.registrations || 0;
                const capacityPct = getCapacityPercentage(registrations, evt.maxCapacity);

                return (
                  <div
                    key={evt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '32px',
                      position: 'relative',
                      flexDirection: isMobile
                        ? 'column'
                        : (isLeft ? 'row' : 'row-reverse'),
                      ...(isMobile ? { paddingLeft: '48px' } : {}),
                    }}
                  >
                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute',
                      left: isMobile ? '20px' : '50%',
                      top: '24px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: '3px solid #c9a84c',
                      backgroundColor: '#0a1f14',
                      transform: isMobile ? 'translateX(-50%)' : 'translateX(-50%)',
                      zIndex: 2,
                    }} />

                    {/* Event card */}
                    <div style={{
                      width: isMobile ? '100%' : 'calc(50% - 48px)',
                      backgroundColor: '#132b1e',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      transition: 'all 0.3s',
                    }}>
                      {/* Accent bar */}
                      <div style={{
                        height: '4px',
                        background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)`,
                      }} />

                      {/* Card body */}
                      <div style={{ padding: '20px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <h3 style={{
                            fontSize: '17px',
                            fontWeight: 700,
                            color: '#f0f4f1',
                            marginBottom: '6px',
                            margin: '0 0 6px 0',
                            flex: 1,
                          }}>
                            {evt.name}
                          </h3>
                          <button
                            onClick={(e) => handleInfoClick(evt, e)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              fontSize: '16px',
                              lineHeight: 1,
                              borderRadius: '4px',
                              opacity: 0.7,
                              flexShrink: 0,
                            }}
                            title="View agenda & parking info"
                            aria-label={`View agenda and parking info for ${evt.name}`}
                          >
                            ℹ️
                          </button>
                        </div>

                        {/* Info popup */}
                        {infoPopup?.eventId === evt.id && (
                          <div style={{
                            backgroundColor: '#1a3d2b',
                            border: '1px solid rgba(201,168,76,0.3)',
                            borderRadius: '8px',
                            padding: '12px 14px',
                            marginBottom: '12px',
                            fontSize: '12px',
                            color: '#a3b8a9',
                          }}>
                            {infoPopup.loading ? (
                              <span>Loading...</span>
                            ) : (
                              <>
                                {infoPopup.agenda.length > 0 && (
                                  <div style={{ marginBottom: infoPopup.parkingInfo ? '10px' : '0' }}>
                                    <div style={{ fontWeight: 700, color: '#c9a84c', marginBottom: '6px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Agenda</div>
                                    {infoPopup.agenda.map((item, i) => (
                                      <div key={i} style={{ marginBottom: '4px', paddingLeft: '8px', borderLeft: '2px solid rgba(201,168,76,0.3)' }}>
                                        <span style={{ color: '#f0f4f1', fontWeight: 600 }}>{item.title}</span>
                                        <span style={{ marginLeft: '6px', color: '#6b8572' }}>
                                          {formatTime(item.startTime)} – {formatTime(item.endTime)}
                                        </span>
                                        {item.speaker && <span style={{ marginLeft: '6px', color: '#6b8572' }}>· {item.speaker}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {infoPopup.agenda.length === 0 && !infoPopup.parkingInfo && (
                                  <span style={{ fontStyle: 'italic' }}>No agenda or parking info available</span>
                                )}
                                {infoPopup.parkingInfo && (
                                  <div>
                                    <div style={{ fontWeight: 700, color: '#c9a84c', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🅿️ Parking</div>
                                    <div style={{ paddingLeft: '8px', color: '#a3b8a9' }}>{infoPopup.parkingInfo}</div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        <div style={{
                          fontSize: '12px',
                          color: '#6b8572',
                          marginBottom: '12px',
                          display: 'flex',
                          flexDirection: 'column' as const,
                          gap: '3px',
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            🕐 {formatTime(evt.startTime)} – {formatTime(evt.endTime)}
                          </span>
                          {evt.venue && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              📍 {evt.venue.name}
                            </span>
                          )}
                        </div>

                        {/* Capacity indicator */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap' as const,
                        }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            color: '#a3b8a9',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            <span>
                              <strong style={{ color: '#f0f4f1', fontWeight: 700 }}>
                                {registrations}
                              </strong>/{evt.maxCapacity}
                            </span>
                            <div style={{
                              width: '60px',
                              height: '5px',
                              backgroundColor: 'rgba(255,255,255,0.05)',
                              borderRadius: '3px',
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${capacityPct}%`,
                                borderRadius: '3px',
                                background: 'linear-gradient(90deg, #1a6b3c, #c9a84c)',
                              }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Summary Footer */}
        <div style={{
          marginTop: '60px',
          textAlign: 'center' as const,
          padding: '32px',
          backgroundColor: '#132b1e',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <h3 style={{
            fontSize: '18px',
            color: '#f0f4f1',
            marginBottom: '16px',
            margin: '0 0 16px 0',
          }}>
            Reunion at a Glance
          </h3>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '32px',
            flexWrap: 'wrap' as const,
          }}>
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#c9a84c' }}>
                {aggregates.totalEvents}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#6b8572',
                textTransform: 'uppercase' as const,
                letterSpacing: '1px',
              }}>
                Events
              </div>
            </div>
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#c9a84c' }}>
                {aggregates.uniqueDates}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#6b8572',
                textTransform: 'uppercase' as const,
                letterSpacing: '1px',
              }}>
                Days
              </div>
            </div>
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#c9a84c' }}>
                {aggregates.uniqueVenues}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#6b8572',
                textTransform: 'uppercase' as const,
                letterSpacing: '1px',
              }}>
                Venues
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Internal helper for date label formatting ---

function formatDateLabel(dateKey: string): string {
  const d = new Date(dateKey + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}
